import {
    PortfolioEnterpriseProductPriceAdjustment,
    PortfolioPercentagePriceAdjustmentUpsertParameters,
    PortfolioAmountPart,
    PortfolioFixedPriceAdjustmentData,
    PortfolioPercentagePriceAdjustmentData,
    PortfolioAmount,
} from '@mews-commander/data-access';
import { EditServicesProductProps, ServicesProductFormValues } from '../types';
import type {
    MapFormPriceAdjustmentToApiParams,
    FixedPriceAdjustmentForm,
    PercentagePriceAdjustmentForm,
    PriceAdjustmentApi,
    MapFixedFormPriceAdjustmentParams,
} from '../types';
import { PriceAdjustmentDiscriminator } from '../schemas';

// --- Type guards for discriminated union ---
const isFixedPriceAdjustment = (
    priceAdjustmentValue: ServicesProductFormValues['priceAdjustment']
): priceAdjustmentValue is {
    discriminator: PriceAdjustmentDiscriminator.Fixed;
    fixedValue: FixedPriceAdjustmentForm;
} =>
    !!priceAdjustmentValue &&
    priceAdjustmentValue.discriminator === PriceAdjustmentDiscriminator.Fixed &&
    'fixedValue' in priceAdjustmentValue;

const isPercentagePriceAdjustment = (
    priceAdjustmentValue: ServicesProductFormValues['priceAdjustment']
): priceAdjustmentValue is {
    discriminator: PriceAdjustmentDiscriminator.Percentage;
    percentageValue: PercentagePriceAdjustmentForm;
} =>
    !!priceAdjustmentValue &&
    priceAdjustmentValue.discriminator === PriceAdjustmentDiscriminator.Percentage &&
    'percentageValue' in priceAdjustmentValue;

/**
 * Maps a fixed price adjustment for the form default values.
 * Uses the adjustment value if present, otherwise falls back to basePriceValue.
 */
const mapFixedDefault = (
    basePriceValue: PortfolioAmount,
    value?: PortfolioFixedPriceAdjustmentData
): {
    discriminator: PriceAdjustmentDiscriminator.Fixed;
    fixedValue: { value: number };
} => {
    return {
        discriminator: PriceAdjustmentDiscriminator.Fixed,
        fixedValue: {
            value: value?.price ?? basePriceValue.netValue,
        },
    };
};

/**
 * Maps a percentage price adjustment for the form default values.
 */
const mapPercentageDefault = (
    value: PortfolioPercentagePriceAdjustmentData
): {
    discriminator: PriceAdjustmentDiscriminator.Percentage;
    percentageValue: { amountPart: PortfolioAmountPart; multiplier: number };
} => {
    return {
        discriminator: PriceAdjustmentDiscriminator.Percentage,
        percentageValue: {
            amountPart: value.target,
            multiplier: value.multiplier,
        },
    };
};

/**
 * Maps the adjustment and base price from the product to the default form values for the edit form.
 * If adjustment is present, uses its value; otherwise, returns a 'None' discriminator.
 */
export const mapAdjustmentDefaultValues = ({
    basePriceValue,
    adjustment,
}: {
    basePriceValue: PortfolioAmount;
    adjustment?: EditServicesProductProps['adjustment'];
}): ServicesProductFormValues['priceAdjustment'] => {
    if (adjustment) {
        const { value, discriminator } = adjustment;
        if (discriminator === PortfolioEnterpriseProductPriceAdjustment.Fixed) {
            return mapFixedDefault(basePriceValue, value);
        }
        if (discriminator === PortfolioEnterpriseProductPriceAdjustment.Percentage) {
            return mapPercentageDefault(value);
        }
    }
    // Default to 'None' if no adjustment is provided
    return { discriminator: PriceAdjustmentDiscriminator.None };
};

const mapFixedFormPriceAdjustment = ({
    fixedValue,
    basePriceValue,
    priceAdjustmentId,
}: MapFixedFormPriceAdjustmentParams): PriceAdjustmentApi => ({
    discriminator: PortfolioEnterpriseProductPriceAdjustment.Fixed,
    value: {
        priceAdjustmentId,
        currencyCode: basePriceValue.currency,
        value: fixedValue.value,
    },
});

/**
 * Maps a percentage price adjustment from form values to the API contract.
 */
const mapPercentageFormPriceAdjustment = (
    percentageValue: PercentagePriceAdjustmentForm,
    priceAdjustmentId: string | null
): PriceAdjustmentApi => {
    const apiValue: PortfolioPercentagePriceAdjustmentUpsertParameters = {
        priceAdjustmentId,
        amountPart: percentageValue.amountPart,
        multiplier: percentageValue.multiplier,
    };
    return {
        discriminator: PortfolioEnterpriseProductPriceAdjustment.Percentage,
        value: apiValue,
    };
};

const hasPriceAdjustmentTypeChanged = (
    current: PriceAdjustmentDiscriminator,
    original?: keyof typeof PortfolioEnterpriseProductPriceAdjustment
): boolean => {
    if (!original) return false;
    const originalType = original === PortfolioEnterpriseProductPriceAdjustment.Fixed ? 'Fixed' : 'Percentage';
    const currentType = current === PriceAdjustmentDiscriminator.Fixed ? 'Fixed' : 'Percentage';
    return originalType !== currentType;
};

/**
 * Maps form price adjustment values to the API contract for update requests.
 * - If no adjustment or discriminator is 'None', returns undefined.
 * - If the adjustment type changes, nullifies priceAdjustmentId.
 * - Always uses basePriceValue for currency fallback in fixed adjustments.
 */
export const mapFormPriceAdjustmentToApi = ({
    priceAdjustment: priceAdjustmentValue,
    basePriceValue,
    originalAdjustment,
}: MapFormPriceAdjustmentToApiParams): PriceAdjustmentApi | undefined => {
    if (!priceAdjustmentValue || priceAdjustmentValue.discriminator === PriceAdjustmentDiscriminator.None)
        return undefined;

    const typeChanged = hasPriceAdjustmentTypeChanged(
        priceAdjustmentValue.discriminator,
        originalAdjustment?.discriminator
    );

    if (isFixedPriceAdjustment(priceAdjustmentValue)) {
        const priceAdjustmentId =
            !typeChanged && originalAdjustment?.discriminator === PortfolioEnterpriseProductPriceAdjustment.Fixed
                ? originalAdjustment.value?.priceAdjustmentId ?? null
                : null;
        return mapFixedFormPriceAdjustment({
            fixedValue: priceAdjustmentValue.fixedValue,
            basePriceValue,
            priceAdjustmentId,
        });
    }

    if (isPercentagePriceAdjustment(priceAdjustmentValue)) {
        const priceAdjustmentId =
            !typeChanged && originalAdjustment?.discriminator === PortfolioEnterpriseProductPriceAdjustment.Percentage
                ? originalAdjustment.value?.priceAdjustmentId ?? null
                : null;
        return mapPercentageFormPriceAdjustment(priceAdjustmentValue.percentageValue, priceAdjustmentId);
    }

    return undefined;
};
